import _ from 'lodash';
import { STAFF_PATHS } from '../../../../_configuration';
import React, { useCallback, useMemo } from 'react';

// Material UI
import Box from '@material-ui/core/Box';
import EditIcon from '@material-ui/icons/Edit';
import IconButton from '@material-ui/core/IconButton';
import WarningIcon from '@material-ui/icons/Warning';

// Data
import { numToTime } from '../../../lib/helpers/conversion.helpers';

// Components
import { EnhancedTable, IEnhancedTableRow, TableCell } from '../../_core/_ui/table.components';
import { FormInput } from '../../_core/_ui/forms.component';
import { Spacer } from '../../_core/_ui/structure.components';
import { RsvpSummaryCell } from '../../Attendance/rsvp-summary-cell.component';
import { IceConflictWarning } from '../ice-conflict-warning.component';
import { pluralTerm } from '../../../lib/helpers/term.helper';
import Tooltip from '@material-ui/core/Tooltip';
import UnarchiveIcon from '@material-ui/icons/Unarchive';
import DeleteIcon from '@material-ui/icons/Delete';
import { PrimaryButton, SecondaryButton } from '../../_core/_ui/buttons.component';
import ArchiveIcon from '@material-ui/icons/Archive';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import { generatePath, useHistory } from 'react-router';
import { getAuthUser } from '../../../lib/services/auth.service';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SportTerminology } from '../../../lib/types';
import { Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { gql, useMutation } from '@apollo/client';
import { LockOpenOutlined, LockOutlined } from '@material-ui/icons';

export const SEND_SESSION_COMMUNICATION = gql`
  mutation SendSessionCommunication($assessmentSessionId: ID!, $type: String!, $playerAssessmentId: ID) {
    sendSessionCommunication(assessmentSessionId: $assessmentSessionId, type: $type, playerAssessmentId: $playerAssessmentId)
  }
`

const buttonStyle: React.CSSProperties = { whiteSpace: "nowrap" }

interface IAssessmentSessionList {
  assessmentSessions: Array<IAssessmentSession>;
  countArchivedSessions: number;
  countClosedSessions: number;
  search: Function;
  loading: boolean;
  fetchError: boolean;
  selected: IAssessmentSession[];
  setSelected: Function;
  viewingArchived: boolean;
  setViewingArchived: Function;
  viewingClosed: boolean;
  setViewingClosed: Function;
  removeSessions: Function;
  restoreSessions: Function;
  closeSessions: Function;
  openSessions: Function;
}

export const AssessmentSessionList: React.FC<IAssessmentSessionList> = ({
  assessmentSessions,
  search,
  loading,
  fetchError,
  selected,
  setSelected,
  countArchivedSessions,
  countClosedSessions,
  viewingArchived,
  setViewingArchived,
  viewingClosed,
  setViewingClosed,
  removeSessions,
  restoreSessions,
  closeSessions,
  openSessions
}) => {
  const termLookup = useCompanySportTerminology();
  const sessionTerm = termLookup[SportTerminology.SESSION];
  const sessionsTerm = pluralTerm(sessionTerm)

  const theme = useTheme()
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'));

  const [sendSessionCommunication, {loading: sendingCommunication}] = useMutation(SEND_SESSION_COMMUNICATION)

  const user = getAuthUser();
  const history = useHistory();
  const rows: IEnhancedTableRow[] = useMemo(() => {
    const newRows: IEnhancedTableRow[] = [];
    _.each(assessmentSessions, (assessmentSession: IAssessmentSession) => {
      const drillCount = assessmentSession.drillCount ?? 0;
      const practicePlanCount = assessmentSession.practicePlanCount ?? 0;
      const countColor = drillCount + practicePlanCount === 0 ? 'error' : 'textPrimary';

      newRows.push({
        _id: assessmentSession._id,
        data: assessmentSession,
        cells: [
        <TableCell key="actions" padding="checkbox">
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <IconButton onClick={() => history.push(generatePath(STAFF_PATHS.ASSESSMENT_SESSION, { assessmentSessionId: assessmentSession._id }))}>
              <EditIcon/>
            </IconButton>
            {!!assessmentSession.unappliedActionsCount && (
              <Tooltip title={`Found ${assessmentSession.unappliedActionsCount} new scores`}>
                <WarningIcon color='error' />
              </Tooltip>
            )}
            <IceConflictWarning conflicts={assessmentSession.iceConflicts} />
          </Box>
        </TableCell>,
        <TableCell key="ageGroup">{assessmentSession.ageGroup.name}</TableCell>,
        <TableCell key="playerCount">{assessmentSession.playerCount}</TableCell>,
        <TableCell key="drillCount"><Typography color={countColor}>{assessmentSession.drillCount}</Typography></TableCell>,
        <TableCell key="practicePlanCount"><Typography color={countColor}>{assessmentSession.practicePlanCount}</Typography></TableCell>,
        <TableCell key="date">{assessmentSession.date}</TableCell>,
        <TableCell key="start">{numToTime(assessmentSession.start)}</TableCell>,
        <TableCell key="duration">{assessmentSession.duration}</TableCell>,
        <TableCell key="location">{assessmentSession.location || '-'}</TableCell>,
        <TableCell key="address">{assessmentSession.address}</TableCell>,
        <TableCell key="gender">{assessmentSession.gender}</TableCell>,
        <TableCell key="rsvp">
          <RsvpSummaryCell counts={assessmentSession.isAttendanceRequested ? assessmentSession.attendanceCounts : undefined} />
        </TableCell>
        ]
      });
    });

    return newRows;
  }, [assessmentSessions])

  const columns = [
    { key: 'actions', disableSort: true, disablePadding: true, width: '80px' },
    { key: 'ageGroup', disablePadding: true, label: 'Age' },
    { key: 'playerCount', disablePadding: true, label: pluralTerm(termLookup[SportTerminology.ATHLETE]) },
    { key: 'drillCount', disablePadding: true, label: 'Drills' },
    { key: 'practicePlanCount', disablePadding: true, label: 'Practice Plans' },
    { key: 'date', disablePadding: true, label: 'Date', width: '112px' },
    { key: 'time', disablePadding: true, label: 'Time', width: '92px' },
    { key: 'duration', disablePadding: true, label: 'Duration' },
    { key: 'location', disablePadding: true, label: 'Location' },
    { key: 'address', disablePadding: true, label: 'Address' },
    { key: 'gender', disablePadding: true, label: 'Gender' },
    { key: 'rsvp', disableSort: true, disablePadding: true, label: 'RSVP', helper: 'Shown as attending/pending/not-attending' },
  ];

  const sendNotifications = useCallback((type: string) => {
    if (window.confirm(`Send ${sessionTerm.toLowerCase()} notification to all players?`))
      Promise
        .all(selected.map((s) => sendSessionCommunication({variables: { assessmentSessionId: s._id, type }})))
        .catch(() => alert("Failed to send notifications"))
  }, [selected, sendSessionCommunication, sessionTerm])

  return (
    <>
      <Box mb={2} display="flex">
        <FormInput placeholder={`Search ${sessionsTerm.toLowerCase()}...`} onKeyUp={(e: any) => search(e.currentTarget.value)} />
      </Box>
      <EnhancedTable
        numCols={columns.length}
        columns={columns}
        rows={rows}
        defaultOrder="date"
        entityName={sessionsTerm.toLowerCase()}
        showError={fetchError}
        showLoading={loading}
        title={viewingArchived ? `Archived ${sessionsTerm}` : sessionsTerm}
        selected={selected}
        setSelected={setSelected}
        selectedAction={(
          <Box display="flex" flexWrap={isLarge ? undefined : "wrap"}>
            <Box display="flex" p={2} style={{ height: '72px'}}>
              <PrimaryButton
                disabled={sendingCommunication}
                style={buttonStyle}
                onClick={() => sendNotifications("SESSION_START")}
              >
                Resend Assignment Notifications
              </PrimaryButton>
              <Box mr={4} />
              <PrimaryButton
                disabled={sendingCommunication}
                style={buttonStyle}
                onClick={() => sendNotifications("SESSION_REMINDER")}
              >
                Send Reminder Notifications
              </PrimaryButton>
              <Box mr={4} />
              {!viewingClosed && (
                <Tooltip title={viewingArchived ? 'Restore' : 'Delete'}>
                  <IconButton aria-label="delete" onClick={() => {
                    const _ids = selected.map(item => item._id);
                    viewingArchived ? restoreSessions(_ids) : removeSessions(_ids);
                    setSelected([]);
                  }}>
                    {viewingArchived ? <UnarchiveIcon /> : <DeleteIcon />}
                  </IconButton>
                </Tooltip>
              )}
              {!viewingArchived && (
                <Tooltip title={viewingClosed ? 'Open' : 'Close'}>
                  <IconButton aria-label="close" onClick={() => {
                    const _ids = selected.map(item => item._id);
                    viewingClosed ? openSessions(_ids) : closeSessions(_ids);
                    setSelected([]);
                  }}>
                    {viewingClosed ? <LockOpenOutlined /> : <LockOutlined />}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        )}
        filterComponent={(
          <FormInput
            placeholder={viewingArchived ? `Search archived ${sessionsTerm}...` : `Search ${sessionsTerm}...`}
            onKeyUp={(e: any) => search(e.currentTarget.value)} />
        )}
        noResultsMessage={user?.settings.activeAgeGroupIds.length ? 'No Results Found' : 'No Age Groups Selected'}
      />
      <Box display="flex" justifyContent="flex-end" style={{ gap: "0.5rem" }}>
        {countArchivedSessions > 0 && !viewingArchived && (
          <Box display="flex">
            <Spacer />
            <SecondaryButton
              startIcon={<ArchiveIcon />}
              onClick={() => {
                setViewingArchived(true);
                setViewingClosed(false);
              }}
            >
              View Archived {sessionsTerm}</SecondaryButton>
          </Box>
        )}
        {countClosedSessions > 0 && !viewingClosed && (
          <Box display="flex">
            <Spacer />
            <SecondaryButton
              startIcon={<LockOutlined />}
              onClick={() => {
                setViewingArchived(false);
                setViewingClosed(true);
              }}
            >
              View Closed {sessionsTerm}
            </SecondaryButton>
          </Box>
        )}
        {(viewingArchived || viewingClosed) && (
          <Box display="flex">
            <Spacer />
            <PrimaryButton
              startIcon={<ArrowBackIosIcon />}
              onClick={() => {
                setViewingClosed(false);
                setViewingArchived(false);
              }}
            >
              Back to {sessionsTerm}
            </PrimaryButton>
          </Box>
        )}
      </Box>
    </>
  );
};

